"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { ref, onValue, push, set, remove, get, update } from "firebase/database"
import { auth, db } from "../Firebase/firebase-config"
import { getAuth } from "firebase/auth"
import {
  IoAttachOutline,
  IoCreateOutline,
  IoDocumentOutline,
  IoSend,
  IoTrashOutline,
  IoSearchOutline,
  IoEllipsisVerticalOutline,
  IoCloseOutline,
  IoDownloadOutline,
  IoPersonOutline,
  IoArrowUpOutline,
  IoArrowDownOutline,
} from "react-icons/io5"
import { FaReply } from "react-icons/fa"
import "./Chatbot.css"
import { useCurrentUid } from "../hooks/useCurrentUid"
import { useAccount } from "../context/AccountContext"
import FileStorage from "./FileStorage"
import { useLocation } from "react-router-dom"

const Chatbot = ({ isGuest = false }) => {
  const location = useLocation()
  const isPartnerDashboard = location.pathname === "/partner-dashboard" || location.pathname.startsWith("/partner-dashboard")
  const switchedUid = useCurrentUid()  // This hook runs always

  // Compute the final UID using useMemo (no early return here!)
  const currentUid = useMemo(() => {
    if (isPartnerDashboard) {
      const auth = getAuth()
      const user = auth.currentUser
      return user ? user.uid : null
    }
    return switchedUid
  }, [isPartnerDashboard, switchedUid])


  const { accountSwitchTrigger, loading: accountLoading } = useAccount()
  const [activeMessageForActions, setActiveMessageForActions] = useState(null); // will store the msg object or null
  const [userId, setUserId] = useState(null)
  const [guestId, setGuestId] = useState(null)
  const [chats, setChats] = useState([])
  const [groupChats, setGroupChats] = useState([])
  const [selectedChat, setSelectedChat] = useState(null)
  const [message, setMessage] = useState("")
 const [userWantsToStayAtBottom, setUserWantsToStayAtBottom] = useState(true);
  const [hasNewMessagesWhileScrolledUp, setHasNewMessagesWhileScrolledUp] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [showFilePreview, setShowFilePreview] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [senderProfiles, setSenderProfiles] = useState({})
  const [activeMsgId, setActiveMsgId] = useState(null)
  const [showQueryModal, setShowQueryModal] = useState(false)
  const [showMenuDropdown, setShowMenuDropdown] = useState(false)
  const [showSearchBar, setShowSearchBar] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const [searchResults, setSearchResults] = useState([])
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1)
  const [replyingTo, setReplyingTo] = useState(null)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [documentOverlay, setDocumentOverlay] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [activeMediaTab, setActiveMediaTab] = useState("media")
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState(null)
  const [selectedMessage, setSelectedMessage] = useState(null);   // the message we double-clicked
  const [showMessageActions, setShowMessageActions] = useState(false);
  const [actionMenuPos, setActionMenuPos] = useState({ x: 0, y: 0 });

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)
  const dragCounter = useRef(0)

  const predefinedMessages = [
    "Where is my truck?",
    "What's the status of my order?",
    "Can I get a quote for cement?",
    "Can you share the cement rates as per grade or site?",
    "How long will delivery take to my site?",
    "Is bulk pricing available?",
    "Is the required material currently available?",
    "Can you share the latest bank statements?",
    "Do you have both small and large trucks available for delivery?",
    "Can you provide the MTC reports of the dispatched material?",
    "Can you confirm my payment status?",
    "What are your payment terms?",
    "Can you provide technical specifications?",
    "What's the minimum order quantity?",
    "Do you deliver on weekends?",
    "What's the quality certification?",
    "Can I schedule a site visit?",
  ]



  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? "smooth" : "instant",
      })
    }
  }, [])


  const handleReply = useCallback((message) => {
    setReplyingTo(message)
    inputRef.current?.focus()
  }, [])

  const cancelReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  const getTimestampMs = (timestamp) => {
    let ts = typeof timestamp === "number" ? timestamp : Number.parseInt(timestamp, 10)
    if (isNaN(ts)) return ""
    if (ts < 1e12) ts *= 1000
    return ts
  }

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (dragCounter.current > 0) {
      setDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "copy"
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    const validFiles = files.filter(validateFile)

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
      setShowFilePreview(true)
    }
  }, [])

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items
    const files = []
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind === "file") {
        const file = item.getAsFile()
        if (file && validateFile(file)) {
          files.push(file)
        }
      }
    }
    if (files.length > 0) {
      e.preventDefault()
      setSelectedFiles((prev) => [...prev, ...files])
      setShowFilePreview(true)
    }
  }, [])

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() || !selectedChat) {
      setSearchResults([])
      setCurrentSearchIndex(-1)
      return
    }

    const results = selectedChat.messages
      .filter(
        (msg) =>
          msg.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          msg.reply?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp))

    setSearchResults(results)
    setCurrentSearchIndex(results.length > 0 ? 0 : -1)
  }, [searchQuery, selectedChat])

  const handleNavigateSearch = (direction) => {
    if (searchResults.length === 0) return
    let newIndex = currentSearchIndex + direction
    if (newIndex < 0) newIndex = searchResults.length - 1
    if (newIndex >= searchResults.length) newIndex = 0
    setCurrentSearchIndex(newIndex)
  }

  const handleScrollToMessage = useCallback((msgId) => {
    const msgElement = document.getElementById(`msg-${msgId}`)
    if (msgElement) {
      msgElement.scrollIntoView({ behavior: "smooth", block: "center" })
      msgElement.classList.add("uc-highlight")
      setTimeout(() => msgElement.classList.remove("uc-highlight"), 2000)
    }
  }, [])

  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults[currentSearchIndex]) {
      handleScrollToMessage(searchResults[currentSearchIndex].id)
    }
  }, [currentSearchIndex, searchResults, handleScrollToMessage])


  const handleShowProfile = useCallback(() => {
    setShowProfileModal(true)
    setShowMenuDropdown(false)
  }, [])

  const handleShowMedia = useCallback(() => {
    setShowMediaModal(true)
    setShowMenuDropdown(false)
  }, [])

  const handleClearChat = useCallback(async () => {
    if (!selectedChat || !currentUid) return

    if (!window.confirm("Are you sure you want to clear this chat? This action cannot be undone.")) return

    try {
      if (selectedChat.isGroup) {
        const messagesRef = ref(db, `groupChats/${selectedChat.groupId}/messages`)
        await set(messagesRef, {})
      } else {
        const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
        const chatRef = ref(db, `${chatPath}/${selectedChat.chatId}`)
        await set(chatRef, { createdAt: Date.now(), system: "chat cleared" })
      }
      setShowMenuDropdown(false)
    } catch (error) {
      console.error("Error clearing chat:", error)
      alert("Error clearing chat. Please try again.")
    }
  }, [selectedChat, currentUid, isGuest])

  const handleDocumentClick = useCallback((file) => {
    setDocumentOverlay(file)
  }, [])

  // Clear all chat data when account (currentUid) switches
  useEffect(() => {
    setChats([])
    setGroupChats([])
    setSelectedChat(null)
    setMessage("")
    setSelectedFiles([])
    setShowFilePreview(false)
    setReplyingTo(null)
    setSearchQuery("")
    setSearchResults([])
    setCurrentSearchIndex(-1)
    // Optional: close modals
    setShowQueryModal(false)
    setShowProfileModal(false)
    setShowMediaModal(false)
  }, [currentUid])

  const handleDownloadFile = useCallback((file) => {
    const link = document.createElement("a")
    link.href = file.url
    link.target = "_blank"
    link.rel = "noopener noreferrer"

    link.download = file.name || "file"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setDocumentOverlay(null)
  }, [])

  const handleCloseChat = useCallback(() => {
    setSelectedChat(null)
    setShowMenuDropdown(false)
  }, [])

  useEffect(() => {
    if (isGuest) {
      let guestUserId = localStorage.getItem("guestChatId")
      if (!guestUserId) {
        guestUserId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem("guestChatId", guestUserId)
      }
      setGuestId(guestUserId)
      setUserId(guestUserId)
    } else {
      const unsubscribe = auth.onAuthStateChanged((currentUid) => {
        if (currentUid) {
          setUserId(currentUid)
        } else {
          setUserId(null)
        }
      })
      return () => unsubscribe()
    }
  }, [isGuest])

  useEffect(() => {
    if (!isGuest && currentUid) {
      setUserId(currentUid)
    } else if (!isGuest && !currentUid) {
      setUserId(null)
    }

  }, [currentUid, isGuest, accountSwitchTrigger])
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    document.addEventListener("dragover", preventDefault)
    document.addEventListener("drop", preventDefault)

    return () => {
      document.removeEventListener("dragover", preventDefault)
      document.removeEventListener("drop", preventDefault)
    }
  }, [])

  useEffect(() => {
    document.addEventListener("dragenter", handleDragEnter)
    document.addEventListener("dragleave", handleDragLeave)
    document.addEventListener("dragover", handleDragOver)
    document.addEventListener("drop", handleDrop)

    return () => {
      document.removeEventListener("dragenter", handleDragEnter)
      document.removeEventListener("dragleave", handleDragLeave)
      document.removeEventListener("dragover", handleDragOver)
      document.removeEventListener("drop", handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 769)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    handleSearch()
  }, [searchQuery, handleSearch])

  const validateFile = (file) => {
    try {
      return FileStorage.validateFile(file)
    } catch (error) {
      alert(error.message)
      return false
    }
  }

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    const validFiles = files.filter(validateFile)

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
      if (inputRef.current) {
        inputRef.current.focus()
      }
      setShowFilePreview(true)
    }

    event.target.value = ""
  }

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    if (selectedFiles.length === 1) {
      setShowFilePreview(false)
    }
  }

  const uploadFile = useCallback(
    async (file) => {
      try {
        if (!selectedChat) throw new Error("No chat selected")

        const chatId = selectedChat?.isGroup ? selectedChat.groupId : selectedChat.chatId

        const fileData = await FileStorage.uploadFile(file, chatId, currentUid)

        return fileData
      } catch (error) {
        console.error("Error uploading file:", error)
        throw error
      }
    },
    [selectedChat, currentUid],
  )

 const handleScroll = useCallback(() => {
  if (!messagesContainerRef.current) return;
  const el = messagesContainerRef.current;

  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

  // Consider "at bottom" only if very close (good for touch devices)
  const isAtBottom = distanceFromBottom < 120;

  setUserWantsToStayAtBottom(isAtBottom);

  // Show floating button when clearly scrolled up
  setShowScrollButton(distanceFromBottom > 400);   // ← increased threshold
}, []);

useEffect(() => {
  if (!selectedChat?.messages?.length || !messagesContainerRef.current) return;

  const container = messagesContainerRef.current;
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

  // Consider "at bottom" if within ~100–150px (mobile-friendly)
  const isPracticallyAtBottom = distanceFromBottom < 150;

  // ────────────────────────────────
  // When should we auto-scroll?
  // 1. User was already near bottom → follow new messages
  // 2. This is the very first load / opening chat
  if (userWantsToStayAtBottom || container.scrollTop < 50) {
    scrollToBottom(true);           // smooth
    setHasNewMessagesWhileScrolledUp(false);
  } else {
    // User is reading older messages → show indicator only
    setHasNewMessagesWhileScrolledUp(true);
  }
}, [selectedChat?.messages, userWantsToStayAtBottom, scrollToBottom]);

  useEffect(() => {
    if (!currentUid) return

    const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
    const userChatsRef = ref(db, chatPath)

    const unsubscribe = onValue(userChatsRef, (snapshot) => {
      const data = snapshot.val() || {}
      const chatList = []

      Object.entries(data).forEach(([key, value]) => {
        if (key.startsWith("chat-") && typeof value === "object") {
          const chatMessages = []
          let unreadCount = 0

          Object.entries(value).forEach(([messageId, messageData]) => {
            if (messageData && messageData.sender && messageData.timestamp) {
              const msg = { ...messageData, timestamp: messageData.timestamp, id: messageId }
              chatMessages.push(msg)

              // Check if current user has read this message
              if (msg.sender !== currentUid && msg.sender !== "user" && msg.sender !== "guest") {
                const readBy = msg.readBy || []
                if (!readBy.includes(currentUid)) {
                  unreadCount++
                }
              }
            }
          })

          chatMessages.sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp))

          chatList.push({
            chatId: key,
            messages: chatMessages,
            lastMessage: chatMessages[chatMessages.length - 1],
            unreadCount,
            uniqueId: `${currentUid}_${key}`,
            isNewChat: chatMessages.length === 0,
          })
        }
      })

      chatList.sort((a, b) => {
        const aTime = a.lastMessage ? getTimestampMs(a.lastMessage.timestamp) : 0
        const bTime = b.lastMessage ? getTimestampMs(b.lastMessage.timestamp) : 0
        return bTime - aTime
      })

      setChats(chatList)

      setSelectedChat((prev) => {
        if (!prev) return prev
        const updatedChat = chatList.find((chat) => chat.uniqueId === prev.uniqueId)
        if (!updatedChat) return prev

        if (JSON.stringify(prev.messages) !== JSON.stringify(updatedChat.messages)) {
          return updatedChat
        }
        return prev
      })
    })

    return () => unsubscribe()
  }, [currentUid, isGuest])

  useEffect(() => {
    if (!currentUid || isGuest) return

    const groupChatsRef = ref(db, "groupChats")
    const unsubscribe = onValue(
      groupChatsRef,
      (snapshot) => {
        const data = snapshot.val()
        const currentId = currentUid
        if (data) {
          const groupChatList = Object.keys(data)
            .map((groupId) => {
              const groupData = data[groupId]

              // Only include groups where user is a member
              if (!groupData.members || !groupData.members.includes(currentId)) return null

              const messages = Object.entries(groupData.messages || {})
                .map(([id, msg]) => ({ id, ...msg }))
                .sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp))

              // Count unread messages for this user
              const unreadCount = messages.filter((msg) => {
                if (msg.sender === currentId) return false
                const readBy = msg.readBy || []
                return !readBy.includes(currentId)
              }).length

              return {
                groupId,
                uniqueId: `group-${groupId}`,
                groupName: groupData.groupName || `Group-${groupId}`,
                members: groupData.members || [],
                messages,
                lastMessage: messages[messages.length - 1],
                unreadCount,
                isGroup: true,
              }
            })
            .filter(Boolean)

          setGroupChats(groupChatList)

          setSelectedChat((prev) => {
            if (prev && prev.isGroup) {
              const updatedChat = groupChatList.find((c) => c.uniqueId === prev.uniqueId)
              if (updatedChat) {
                return updatedChat
              }
            }
            return prev
          })
        } else {
          setGroupChats([])
          setSelectedChat((prev) => {
            if (prev && prev.isGroup) {
              return null
            }
            return prev
          })
        }
      },
      (error) => {
        console.error("Error fetching group chats:", error)
        setGroupChats([])
      },
    )

    return () => unsubscribe()
  }, [currentUid, isGuest])

  useEffect(() => {
    if (!currentUid || isGuest || groupChats.length === 0) return

    const unsubs = []
    const allMembers = new Set()
    groupChats.forEach((group) => {
      group.members.forEach((m) => {
        if (m !== currentUid) allMembers.add(m)
      })
    })

    return () => unsubs.forEach((u) => u())
  }, [groupChats, currentUid, isGuest])

  const fetchSenderProfiles = useCallback(
    async (members) => {
      for (const member of members) {
        if (!senderProfiles[member]) {
          const snap = await get(ref(db, `profileDashboard/${member}`))
          const data = snap.val()
          setSenderProfiles((prev) => ({
            ...prev,
            [member]: {
              name: data?.name || data?.username || member,
              phone: data?.phone,
              email: data?.email,
            },
          }))
        }
      }
    },
    [senderProfiles],
  ) // Added senderProfiles as a dependency

  useEffect(() => {
    if (selectedChat?.members?.length) {
      fetchSenderProfiles(selectedChat.members)
    }
  }, [selectedChat, fetchSenderProfiles]) // Added fetchSenderProfiles as a dependency

  const markMessagesAsRead = useCallback(
    async (chat) => {
      if (!chat || !currentUid) return
      const currentId = isGuest ? guestId : currentUid

      const unreadMessages = chat.messages.filter((msg) => {
        if (chat.isGroup) {
          if (msg.sender === currentId) return false
        } else {
          if (msg.sender === "user" || msg.sender === "guest") return false
        }
        const readBy = msg.readBy || []
        return !readBy.includes(currentId)
      })

      for (const msg of unreadMessages) {
        if (msg.id) {
          let msgRef
          if (chat.isGroup) {
            msgRef = ref(db, `groupChats/${chat.groupId}/messages/${msg.id}`)
          } else {
            const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
            msgRef = ref(db, `${chatPath}/${chat.chatId}/${msg.id}`)
          }

          const readBy = msg.readBy || []
          if (!readBy.includes(currentId)) {
            readBy.push(currentId)
            await update(msgRef, { readBy })
          }
        }
      }
    },
    [currentUid, guestId, isGuest],
  )



  const handleChatSelect = useCallback(async (chat) => {
    setSelectedChat(chat);
    await markMessagesAsRead(chat);

    if (chat.isGroup) {
      await fetchSenderProfiles(chat.members);
    }

    // Reset scroll behavior
    setAutoScrollEnabled(true);
    setHasNewMessagesWhileScrolledUp(false);
    setShowScrollButton(false);

    // Give DOM time to render messages
    setTimeout(() => {
      scrollToBottom(false); // instant on open
    }, 100);

    inputRef.current?.focus();
    setSelectedFiles([])
    setShowFilePreview(false)
    setReplyingTo(null)
    setSearchQuery("")
    setSearchResults([])
    setCurrentSearchIndex(-1)
  },
    [markMessagesAsRead, fetchSenderProfiles],
  )
  useEffect(() => {
    if (selectedChat) {
      markMessagesAsRead(selectedChat);
    }
  }, [selectedChat, markMessagesAsRead]);
  const handleCreateNewChat = useCallback(async () => {
    if (!currentUid) return

    const newChatNumber = chats.length + 1
    const newChatId = `chat-${newChatNumber}`
    const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
    const chatRef = ref(db, `${chatPath}/${newChatId}`)

    await set(chatRef, {
      createdAt: Date.now(),
      system: "new chat created",
    })

    setSelectedChat({
      chatId: newChatId,
      messages: [],
      lastMessage: null,
      unreadCount: 0,
      uniqueId: `${currentUid}_${newChatId}`,
      isNewChat: false,
    })
  }, [currentUid, chats, isGuest])

  const handleDeleteForEveryone = useCallback(async () => {
    if (!deleteMsg || !selectedChat || !currentUid) return

    try {
      let msgRef
      if (selectedChat.isGroup) {
        msgRef = ref(db, `groupChats/${selectedChat.groupId}/messages/${deleteMsg.id}`)
      } else {
        const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
        msgRef = ref(db, `${chatPath}/${selectedChat.chatId}/${deleteMsg.id}`)
      }
      await update(msgRef, { isDeleted: true, message: "This message was deleted", files: null, reply: "" })

      setSelectedChat((prev) => {
        const newMessages = prev.messages.map((m) =>
          m.id === deleteMsg.id ? { ...m, isDeleted: true, message: "This message was deleted", files: null, reply: "" } : m
        )
        const last = newMessages[newMessages.length - 1]
        return { ...prev, messages: newMessages, lastMessage: last }
      })

      setDeleteModalVisible(false)
      setDeleteMsg(null)
    } catch (error) {
      console.error("Error deleting message for everyone:", error)
      alert("Error deleting message. Please try again.")
    }
  }, [deleteMsg, selectedChat, currentUid, isGuest])

  const handleDeleteForMe = useCallback(async () => {
    if (!deleteMsg || !selectedChat || !currentUid) return
    const currentId = isGuest ? guestId : currentUid

    try {
      let msgRef
      if (selectedChat.isGroup) {
        msgRef = ref(db, `groupChats/${selectedChat.groupId}/messages/${deleteMsg.id}`)
      } else {
        const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
        msgRef = ref(db, `${chatPath}/${selectedChat.chatId}/${deleteMsg.id}`)
      }

      const deletedBy = deleteMsg.deletedBy || []
      if (!deletedBy.includes(currentId)) {
        deletedBy.push(currentId)
      }
      await update(msgRef, { deletedBy })

      setSelectedChat((prev) => {
        const newMessages = prev.messages.map((m) =>
          m.id === deleteMsg.id ? { ...m, deletedBy: [...(m.deletedBy || []), currentId] } : m
        )
        return { ...prev, messages: newMessages }
      })

      setDeleteModalVisible(false)
      setDeleteMsg(null)
    } catch (error) {
      console.error("Error deleting message for me:", error)
      alert("Error deleting message. Please try again.")
    }
  }, [deleteMsg, selectedChat, currentUid, isGuest, guestId])

  const handleEditMessage = useCallback(
    (msgId, currentText) => {
      const msg = selectedChat.messages.find((m) => m.id === msgId)
      if (!msg) return

      const timeDiff = Date.now() - getTimestampMs(msg.timestamp)
      if (timeDiff > 15 * 60 * 1000) {
        alert("You can only edit messages within 15 minutes of sending.")
        return
      }

      const newText = window.prompt("Edit your message:", currentText)
      if (!newText || !selectedChat || !currentUid) return

      try {
        let msgRef
        if (selectedChat.isGroup) {
          msgRef = ref(db, `groupChats/${selectedChat.groupId}/messages/${msgId}`)
        } else {
          const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
          msgRef = ref(db, `${chatPath}/${selectedChat.chatId}/${msgId}`)
        }

        // ← THIS IS THE IMPORTANT PART
        update(msgRef, {
          message: newText,
          editedAt: Date.now()
        })

        // Update local state
        setSelectedChat((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === msgId ? { ...m, message: newText, editedAt: Date.now() } : m
          ),
          lastMessage:
            prev.lastMessage?.id === msgId
              ? { ...prev.lastMessage, message: newText, editedAt: Date.now() }
              : prev.lastMessage,
        }))
      } catch (error) {
        console.error("Error editing message:", error)
        alert("Failed to edit message. Please try again.")
      }
    },
    [selectedChat, currentUid, isGuest]
  )

  const handleEndChat = useCallback(async () => {
    if (!selectedChat || !currentUid) return

    const confirmText = selectedChat.isGroup ? "leave this group" : "end and delete this chat"
    if (!window.confirm(`Are you sure you want to ${confirmText}?`)) return

    if (selectedChat.isGroup) {
      const groupRef = ref(db, `groupChats/${selectedChat.groupId}`)
      const snap = await get(groupRef)
      const data = snap.val()
      if (data) {
        const newMembers = data.members.filter((m) => m !== currentUid)
        if (newMembers.length === 0) {
          await remove(groupRef)
        } else {
          await set(ref(db, `groupChats/${selectedChat.groupId}/members`), newMembers)
        }
      }
    } else {
      const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
      const chatRef = ref(db, `${chatPath}/${selectedChat.chatId}`)
      await remove(chatRef)
    }
    setSelectedChat(null)
  }, [currentUid, selectedChat, isGuest])

  const handleSendMessage = useCallback(async () => {
    if ((!message.trim() && selectedFiles.length === 0) || !selectedChat || !currentUid) return

    const newTimestamp = Date.now()
    const currentId = isGuest ? guestId : currentUid
    const sender = selectedChat.isGroup ? currentId : isGuest ? "guest" : "user"

    const replyContent = replyingTo
      ? replyingTo.message || (replyingTo.files?.length ? `File: ${replyingTo.files[0].name}` : "attachment")
      : ""

    const newMessage = {
      sender,
      message: message,
      reply: replyContent,
      replyToId: replyingTo?.id || null,
      timestamp: newTimestamp,
      readBy: [currentId], // Current user has already "read" their own message
      deletedBy: [],
      files: null,
      isGuest: isGuest,
    }

    try {
      const fileData = []
      if (selectedFiles.length > 0) {
        setIsUploading(true)
        for (const file of selectedFiles) {
          const uploadedFile = await uploadFile(file)
          fileData.push({
            id: uploadedFile.id,
            url: uploadedFile.url,
            name: uploadedFile.name,
            type: uploadedFile.type,
            size: uploadedFile.size,
            uploadedBy: currentUid,
            uploadedAt: uploadedFile.uploadedAt,
          })
        }
        newMessage.files = fileData.length > 0 ? fileData : null
        setIsUploading(false)
      } else {
        setSelectedChat((prev) => ({
          ...prev,
          messages: [...prev.messages, newMessage],
          lastMessage: newMessage,
          isNewChat: false,
        }))
        if (autoScrollEnabled) {
          scrollToBottom()
        }
      }

      let chatRef
      if (selectedChat.isGroup) {
        chatRef = ref(db, `groupChats/${selectedChat.groupId}/messages`)
      } else {
        const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
        chatRef = ref(db, `${chatPath}/${selectedChat.chatId}`)
      }

      await push(chatRef, newMessage)
      setMessage("")
      setSelectedFiles([])
      setShowFilePreview(false)
      setUploadProgress({})
      setReplyingTo(null)
    } catch (error) {
      console.error("Error sending message:", error)
      alert("Error sending message. Please try again.")
      setIsUploading(false)
    }
  }, [
    message,
    selectedChat,
    selectedFiles,
    currentUid,
    isGuest,
    guestId,
    replyingTo,
    autoScrollEnabled,
    scrollToBottom,
    uploadFile,
  ])

  const handleSendPredefined = useCallback(
    async (text) => {
      if (!text.trim() || !selectedChat || !currentUid) return

      const newTimestamp = Date.now()
      const currentId = isGuest ? guestId : currentUid
      const sender = selectedChat.isGroup ? currentId : isGuest ? "guest" : "user"

      const newMessage = {
        sender,
        message: text,
        reply: "",
        timestamp: newTimestamp,
        readBy: [currentId],
        deletedBy: [],
        files: null,
        isGuest: isGuest,
      }

      setSelectedChat((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
        lastMessage: newMessage,
        isNewChat: false,
      }))
      scrollToBottom()

      try {
        let chatRef
        if (selectedChat.isGroup) {
          chatRef = ref(db, `groupChats/${selectedChat.groupId}/messages`)
        } else {
          const chatPath = isGuest ? `guestchatbox/${currentUid}` : `userchatbox/${currentUid}`
          chatRef = ref(db, `${chatPath}/${selectedChat.chatId}`)
        }
        await push(chatRef, newMessage)
        setShowQueryModal(false)
      } catch (error) {
        console.error("Error sending predefined message:", error)
        alert("Error sending message. Please try again.")
      }
    },
    [currentUid, selectedChat, isGuest, guestId, scrollToBottom],
  )

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSendMessage()
      }
    },
    [handleSendMessage],
  )

  const handleInputChange = useCallback((e) => {
    setMessage(e.target.value)
  }, [])

  const copyMessageText = useCallback(() => {
    if (!selectedMessage) return;

    let text = selectedMessage.message || selectedMessage.reply || "";

    if (!text && selectedMessage.files?.length > 0) {
      text = `Attachment: ${selectedMessage.files[0].name || "file"}`;
    }

    navigator.clipboard.writeText(text.trim())
      .then(() => {
        alert("Copied to clipboard!");   // ← replace later with nice toast
      })
      .catch(err => {
        console.error("Copy failed", err);
        alert("Could not copy message");
      });

    // Close menu
    setShowMessageActions(false);
    setSelectedMessage(null);
  }, [selectedMessage]);

const handleMessageAction = useCallback((action, msg = null) => {
  const target = msg || activeMessageForActions || selectedMessage;
  if (!target) return;

  switch (action) {
    case "copy":
      let text = target.message || target.reply || "";
      if (!text && target.files?.length > 0) {
        text = `Attachment: ${target.files[0].name || "file"}`;
      }
      if (text) {
        navigator.clipboard.writeText(text.trim())
          .then(() => alert("Copied!"))
          .catch(() => alert("Copy failed"));
      }
      break;

    case "reply":
      handleReply(target);
      break;

    case "edit":
      handleEditMessage(target.id, target.message || target.reply || "");
      break;

    case "delete":
      setDeleteMsg(target);
      setDeleteModalVisible(true);
      break;

    default:
      console.warn("Unknown action");
  }

  // Close action menus
  setActiveMessageForActions(null);
  setShowMessageActions(false);
  setSelectedMessage(null);
}, [handleReply, handleEditMessage]);

const handleMessageDoubleClick = useCallback((e, msg) => {
  e.preventDefault();
  e.stopPropagation();

  // We NO LONGER skip pure files — we want action bar on images/PDFs too

  // Optional: you can still skip if message is deleted or something
  if (msg.isDeleted) return;

  // Toggle action bar
  if (activeMessageForActions?.id === msg.id) {
    setActiveMessageForActions(null);
  } else {
    setActiveMessageForActions(msg);
  }
}, [activeMessageForActions]);

  const formatTime = (timestamp) => {
    const date = new Date(getTimestampMs(timestamp))
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }
  const formatSidebarTimestamp = (timestamp) => {
    if (!timestamp) return ""
    const date = new Date(getTimestampMs(timestamp))
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    const dateOptions = { month: "short", day: "numeric" }
    if (date.getFullYear() !== today.getFullYear()) {
      dateOptions.year = "numeric"
    }
    if (date.toDateString() === today.toDateString()) {
      return timeStr
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${timeStr}`
    } else {
      const dateStr = date.toLocaleDateString([], dateOptions)
      return `${dateStr} ${timeStr}`
    }
  }

  const formatChatDate = (date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return "Today"
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday"
    } else {
      return date.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    }
  }

  const getMessageStatus = (message, isUser) => {
    if (isUser) {
      const readBy = message.readBy || []
      const isRead = readBy.length > 1 // More than just the sender
      return <span className={`uc-message-status ${isRead ? "uc-read" : "uc-sent"}`}>{isRead ? "✓✓" : "✓"}</span>
    }
    return null
  }

const renderFileAttachment = (file) => {
  const isImage = file.type.startsWith("image/");

  return (
    <div key={file.id || file.url} className="uc-file-attachment">
      {isImage ? (
        <>
          <img
            src={file.url || "/placeholder.svg"}
            alt={file.name}
            className="uc-attached-image"
            onClick={() => handleDocumentClick(file)}
          />
          <button
            className="uc-download-btn small"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadFile(file);
            }}
            title="Download"
          >
            <IoDownloadOutline />
          </button>
        </>
      ) : (
        <div className="uc-attached-file" onClick={() => handleDocumentClick(file)}>
          <IoDocumentOutline className="uc-file-icon" />
          <span className="uc-file-name">{file.name}</span>
          <span className="uc-file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>

          <button
            className="uc-download-btn small"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadFile(file);
            }}
            title="Download"
          >
            <IoDownloadOutline />
          </button>
        </div>
      )}
    </div>
  );
};
 const renderMessageWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="uc-message-link"
          onClick={(e) => e.stopPropagation()}   // ← important!
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

  const getRepliedText = (replyTo) => {
    const content = replyTo.message || replyTo.reply || ""
    if (content) return content.substring(0, 50) + (content.length > 50 ? "..." : "")
    if (replyTo.files && replyTo.files.length > 0) {
      const file = replyTo.files[0]
      if (file.type.startsWith("image/")) return `Image: ${file.name || "unnamed"}`
      if (file.type === "application/pdf") return `PDF: ${file.name || "unnamed"}`
      return `File: ${file.name || "unnamed"}`
    }
    return "Message"
  }



  const allChats = [...chats, ...groupChats].sort((a, b) => {
    const aTime = a.lastMessage ? getTimestampMs(a.lastMessage.timestamp) : 0
    const bTime = b.lastMessage ? getTimestampMs(b.lastMessage.timestamp) : 0
    return bTime - aTime
  })

  const currentId = isGuest ? guestId : currentUid

  const messageElements = []
  let prevDate = null
  if (selectedChat) {
    selectedChat.messages.forEach((msg) => {
      const isDeletedForMe = msg.deletedBy?.includes(currentId) || false
      if (isDeletedForMe) return // Skip rendering if deleted for me

      const date = new Date(getTimestampMs(msg.timestamp))
      const dateStr = date.toDateString()
      if (dateStr !== prevDate) {
        messageElements.push(
          <div key={`date-${dateStr}-${msg.id}`} className="uc-date-header">
            {formatChatDate(date)}
          </div>,
        )
        prevDate = dateStr
      }
      const isUser = selectedChat.isGroup ? msg.sender === currentId : msg.sender === "user" || msg.sender === "guest"
      const messageText = msg.message || msg.reply
      const senderName = selectedChat.isGroup
        ? isUser
          ? "You"
          : senderProfiles[msg.sender]?.name || "supplier"
        : isUser
          ? "You"
          : "supplier"
      let repliedMsg = null
      const repliedId = msg.replyToId || (msg.replyTo ? msg.replyTo.id : null)
      if (msg.replyToId) {
        repliedMsg = selectedChat.messages.find((m) => m.id === msg.replyToId)
      } else if (msg.replyTo && msg.replyTo.id) {
        repliedMsg = selectedChat.messages.find((m) => m.id === msg.replyTo.id) || msg.replyTo
      }
      const repliedSender = repliedMsg ? repliedMsg.sender : msg.replyTo ? msg.replyTo.sender : null
      const senderPrefix = selectedChat.isGroup
        ? repliedSender === currentId
          ? "You"
          : senderProfiles[repliedSender]?.name || "Supplier"
        : repliedSender === (isGuest ? "guest" : "user")
          ? "You"
          : "Supplier"
      const repliedContent = msg.reply || (repliedMsg ? getRepliedText(repliedMsg) : "")

      if (msg.isDeleted) {
        messageElements.push(
          <div
            id={`msg-${msg.id}`}
            key={msg.id}
            className={`ucMsgContainer ${isUser ? "ucMsgUser" : "ucMsgSupplier"} uc-deleted`}
            // onClick={() => isUser && setActiveMsgId(msg.id)}
            // onDoubleClick={(e) => handleMessageDoubleClick(e, msg)}
            onClick={(e) => {
              // Existing click behavior
              if (isUser) setActiveMsgId(msg.id);

              // Move double-click logic here
              handleMessageDoubleClick(e, msg);
            }}
          >
            {selectedChat.isGroup && <div className="ucMsgSender">{senderName}</div>}
            <div className="ucMsgContent">
              <p className="ucMsgText deleted">This message was deleted</p>
              <div className="ucMsgFooter">
                <span className="ucMsgTime">{formatTime(msg.timestamp)}</span>
              </div>
            </div>
          </div>,
        )
      } else {
        messageElements.push(
          <div
            id={`msg-${msg.id}`}
            key={msg.id}
            className={`ucMsgContainer ${isUser ? "ucMsgUser" : "ucMsgSupplier"}`}
            onClick={(e) => {
              // Existing click behavior
              if (isUser) setActiveMsgId(msg.id);

              // Move double-click logic here
              handleMessageDoubleClick(e, msg);
            }}
          >
            {selectedChat.isGroup && <div className="ucMsgSender">{senderName}</div>}

            <div className="ucMsgContent">
              {(repliedId || repliedContent) && (
                <div className="uc-reply-indicator" onClick={() => repliedId && handleScrollToMessage(repliedId)}>
                  <FaReply className="uc-reply-icon" />
                  <span className="uc-reply-text">
                    {senderPrefix}: {repliedContent}
                  </span>
                </div>
              )}

   {/* text */}
{messageText && (
  <p
    className="ucMsgText"
    onDoubleClick={(e) => handleMessageDoubleClick(e, msg)}
    onClick={(e) => e.stopPropagation()}   // ← prevent bubbling when clicking text/links
  >
    {renderMessageWithLinks(messageText)}
  </p>
)}

{/* files */}
{msg.files && msg.files.map((file) => (
  <div key={file.id} onClick={(e) => e.stopPropagation()}>
    {renderFileAttachment(file)}
  </div>
))}

    <div className="ucMsgFooter">
      <span className="ucMsgTime">
        {formatTime(msg.timestamp)} {getMessageStatus(msg, isUser)}
        {msg.editedAt && <span className="uc-edited-label">(edited)</span>}
      </span>
    </div>
  </div>
 {/* Action Bar - Inline (appears below message) */}
{activeMessageForActions?.id === msg.id && (
  <div className="uc-message-action-bar">
    {/* Always show Reply */}
    <button onClick={() => handleMessageAction("reply", msg)}>
      <FaReply /> Reply
    </button>

    {/* Show Copy ONLY if there is text */}
    {(msg.message || msg.reply) && (
      <button onClick={() => handleMessageAction("copy", msg)}>
        Copy
      </button>
    )}

    {/* Only for own messages */}
    {(() => {
      const isOwn = selectedChat.isGroup
        ? msg.sender === currentUid
        : (msg.sender === "user" || msg.sender === "guest");

      if (!isOwn) return null;

      return (
        <>
          {Date.now() - getTimestampMs(msg.timestamp) < 15 * 60 * 1000 && (
            <button onClick={() => handleMessageAction("edit", msg)}>
              Edit
            </button>
          )}
          <button onClick={() => handleMessageAction("delete", msg)}>
            Delete
          </button>
        </>
      );
    })()}

    {/* Cancel Button */}
    <button
      className="uc-action-btn cancel"
      onClick={() => setActiveMessageForActions(null)}
    >
      Cancel
    </button>
  </div>
)}
</div>
        )
      }
    })
  }

  const mediaFiles =
    selectedChat?.messages.flatMap((msg) => msg.files?.filter((f) => f.type.startsWith("image/")) || []) || []
  const docFiles =
    selectedChat?.messages.flatMap((msg) => msg.files?.filter((f) => !f.type.startsWith("image/")) || []) || []
  const links =
    selectedChat?.messages.flatMap((msg) => {
      const text = msg.message || msg.reply || ""
      return text.match(/(https?:\/\/[^\s]+)/g) || []
    }) || []

  return (
    <div className="uc-chatbot-container" ref={dropZoneRef}>
      {dragOver && (
        <div className="uc-drag-overlay">
          <div className="uc-drag-content">
            <IoAttachOutline className="uc-drag-icon" />
            <p>Drop files here to send</p>
          </div>
        </div>
      )}

      <div className={`uc-chat-sidebar ${isMobile && selectedChat ? "uc-hidden" : ""}`}>
        <div className="uc-sidebar-header">
          <div className="uc-sidebar-title">
            <h1>Conversations</h1>
          </div>
          {chats.length === 0 && (
            <button className="uc-new-chat-btn" onClick={handleCreateNewChat}>
              New Chat
            </button>
          )}
        </div>
        <div className="uc-chat-list">
          {allChats.length === 0 ? (
            <div className="uc-empty-chat-list">
              <div className="uc-empty-icon">💬</div>
              <p>No conversations yet</p>
              <span>Messages from suppliers will appear here</span>
            </div>
          ) : (
            allChats.map((chat) => (
              <div
                key={chat.uniqueId}
                onClick={() => handleChatSelect(chat)}
                className={`uc-chat-item ${selectedChat?.uniqueId === chat.uniqueId ? "uc-active" : ""} ${chat.unreadCount > 0 ? "uc-has-unread" : ""}`}
              >
                <div className="uc-chat-avatar">
                  <div className="uc-avatar-circle">{chat.isGroup ? "G" : "S"}</div>

                  {chat.unreadCount > 0 && (
                    <div className="uc-unread-indicator">
                      <span className="uc-unread-count">{chat.unreadCount > 99 ? "99+" : chat.unreadCount}</span>
                    </div>
                  )}
                </div>
                <div className="uc-chat-info">
                  <div className="uc-chat-header">
                    <h3 className="uc-chat-name">{chat.isGroup ? chat.groupName : "Supplier"}</h3>
                    <div className="uc-chat-meta">
                      {chat.lastMessage && (
                        <span className="uc-message-time-stamp">
                          {formatSidebarTimestamp(chat.lastMessage.timestamp)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="uc-chat-preview">
                    <p className={`uc-last-message ${chat.unreadCount > 0 ? "uc-unread-text" : ""}`}>
                      {chat.lastMessage?.sender === (chat.isGroup ? currentId : isGuest ? "guest" : "user")
                        ? "You: "
                        : ""}
                      {chat.lastMessage?.files
                        ? "📎 File attachment"
                        : chat.lastMessage?.message || chat.lastMessage?.reply || "No messages yet"}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className={`uc-chat-main ${isMobile && !selectedChat ? "uc-hidden" : ""}`}>
        {selectedChat ? (
          <>
            <div className="uc-chat-header-main">
              <div className="uc-header-left">
                {isMobile && (
                  <button className="uc-back-btn" onClick={() => setSelectedChat(null)}>
                    Back
                  </button>
                )}
                <div className="uc-header-info" onClick={handleShowProfile}>
                  <div className="uc-chat-avatar">
                    <div className="uc-avatar-circle">{selectedChat.isGroup ? "G" : "S"}</div>
                  </div>
                  <div className="uc-header-text">
                    <h2>{selectedChat.isGroup ? selectedChat.groupName : "Supplier"}</h2>
                  </div>
                </div>
              </div>

              <div className="uc-header-right">
                {!showSearchBar && (
                  <button className="uc-search-btn" onClick={() => setShowSearchBar(true)}>
                    <IoSearchOutline />
                  </button>
                )}
                <div className="uc-menu-container">
                  <button className="uc-menu-btn" onClick={() => setShowMenuDropdown(!showMenuDropdown)}>
                    <IoEllipsisVerticalOutline />
                  </button>
                  {showMenuDropdown && (
                    <div className="uc-menu-dropdown">
                      <button onClick={handleShowProfile}>
                        <IoPersonOutline /> View Profile
                      </button>
                      <button onClick={handleClearChat}>
                        <IoTrashOutline /> Clear Chat
                      </button>
                      {selectedChat.isGroup && (
                        <button onClick={handleEndChat}>
                          <IoTrashOutline /> Exit Group
                        </button>
                      )}
                      <button onClick={handleCloseChat}>
                        <IoCloseOutline /> Close Chat
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {showSearchBar && (
              <div className="uc-search-bar-container">
                <div className="uc-search-container">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search messages..."
                    className="uc-search-input"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleNavigateSearch(1)
                      } else if (e.key === "ArrowUp") {
                        handleNavigateSearch(-1)
                      } else if (e.key === "ArrowDown") {
                        handleNavigateSearch(1)
                      }
                    }}
                  />
                  {searchResults.length > 0 && (
                    <div className="uc-search-nav">
                      <span>
                        {currentSearchIndex + 1}/{searchResults.length}
                      </span>
                      <button onClick={() => handleNavigateSearch(-1)}>
                        <IoArrowUpOutline />
                      </button>
                      <button onClick={() => handleNavigateSearch(1)}>
                        <IoArrowDownOutline />
                      </button>
                    </div>
                  )}
                  <button
                    className="uc-close-search"
                    onClick={() => {
                      setShowSearchBar(false)
                      setSearchQuery("")
                      setSearchResults([])
                      setCurrentSearchIndex(-1)
                    }}
                  >
                    <IoCloseOutline />
                  </button>
                </div>
              </div>
            )}
            <div className="uc-messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
              {selectedChat.messages.length === 0 ? (
                <div className="uc-empty-chat">
                  <div className="uc-empty-icon">💬</div>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <>{messageElements}</>
              )}
              <div ref={messagesEndRef} />
            </div>

            {showFilePreview && selectedFiles.length > 0 && (
              <div className="uc-file-preview-container">
                <div className="uc-file-preview-header">
                  <span>Selected Files ({selectedFiles.length})</span>
                  <button
                    className="uc-close-preview"
                    onClick={() => {
                      setSelectedFiles([])
                      setShowFilePreview(false)
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="uc-file-preview-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="uc-file-preview-item">
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file) || "/placeholder.svg"}
                          alt={file.name}
                          className="uc-preview-image"
                        />
                      ) : (
                        <div className="uc-preview-file">
                          <IoDocumentOutline className="uc-preview-file-icon" />
                        </div>
                      )}
                      <div className="uc-file-info">
                        <span className="uc-file-name">{file.name}</span>
                        <span className="uc-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>

                        {isUploading && (
                          <div className="uc-sending-indicator">
                            <div className="uc-typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                            Sending...
                          </div>
                        )}
                      </div>
                      <button className="uc-remove-file" onClick={() => removeFile(index)}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

<button
  className={`uc-scroll-to-bottom ${showScrollButton && hasNewMessagesWhileScrolledUp ? "" : "hidden"}`}
  onClick={() => {
    scrollToBottom(true);
    // Optional: reset the "new messages" flag after user clicks
    setHasNewMessagesWhileScrolledUp(false);
  }}
>
  ↓ 
</button>

            {replyingTo && (
              <div className="uc-reply-preview">
                <div className="uc-reply-preview-content">
                  <FaReply className="uc-reply-preview-icon" />

                  <p>
                    {replyingTo.message
                      ? replyingTo.message
                      : replyingTo.reply
                        ? replyingTo.reply
                        : replyingTo.files?.length
                          ? `File: ${replyingTo.files[0]?.name || "attachment"}`
                          : "File attachment"}
                  </p>

                  <button onClick={cancelReply} className="uc-cancel-reply">
                    <IoCloseOutline />
                  </button>
                </div>
              </div>
            )}

            <div className="uc-message-input-container">

              <div className="uc-input-wrapper">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpg,image/jpeg,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                <button className="uc-attach-btn" onClick={() => fileInputRef.current?.click()} title="Attach files">
                  <IoAttachOutline />
                </button>
                <button className="uc-query-btn" onClick={() => setShowQueryModal(true)} title="Choose query">
                  ?
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePaste}
                  placeholder="Type a message..."
                  className="uc-message-input"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!message.trim() && selectedFiles.length === 0}
                  className="uc-send-btn"
                >
                  <IoSend />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="uc-empty-state">
            <div className="uc-empty-content">
              <div className="uc-empty-icon">💬</div>
              <h3>Welcome to Your Messages</h3>
              <p>
                {isGuest ? "Chat as a guest with suppliers" : "Select a conversation to start chatting with suppliers"}
              </p>
            </div>
          </div>
        )}
      </div>

      {showQueryModal && (
        <div className="uc-modal-overlay" onClick={() => setShowQueryModal(false)}>
          <div className="uc-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="uc-modal-header">
              <h3>Choose Query</h3>
              <button onClick={() => setShowQueryModal(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="uc-modal-body">
              <div className="uc-query-grid">
                {predefinedMessages.map((text, index) => (
                  <div key={index} className="uc-query-item">
                    <button
                      className="uc-query-option"
                      onClick={() => {
                        setMessage(text)
                        setShowQueryModal(false)
                        inputRef.current?.focus()
                      }}
                    >
                      <IoDocumentOutline className="uc-query-icon" />
                      <span>{text}</span>
                    </button>
                    <button className="uc-send-direct" onClick={() => handleSendPredefined(text)} title="Send directly">
                      <IoSend />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="uc-modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="uc-modal-content uc-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="uc-modal-header">
              <h3>Profile Details</h3>
              <button onClick={() => setShowProfileModal(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="uc-modal-body">
              <div className="uc-profile-info">
                <div className="uc-profile-avatar">
                  <div className="uc-avatar-circle large">{selectedChat?.isGroup ? "G" : "S"}</div>
                </div>
                <h4>{selectedChat?.isGroup ? selectedChat.groupName : "Supplier"}</h4>
                <p className="uc-profile-status">{selectedChat?.isGroup ? "Group Chat" : "Supplier Contact"}</p>
                <div className="uc-profile-details">
                  <div className="uc-detail-item">
                    <span className="uc-detail-label">Messages:</span>
                    <span className="uc-detail-value">{selectedChat?.messages.length || 0}</span>
                  </div>
                  {selectedChat?.isGroup && (
                    <div className="uc-detail-item">
                      <span className="uc-detail-label">Members:</span>
                      <span className="uc-detail-value">
                        {selectedChat.members.map((member) => (
                          <div key={member} className="uc-member-info">
                            {member === currentId ? (
                              <div>You</div>
                            ) : (
                              <>
                                <div>{senderProfiles[member]?.name || member}</div>
                                {senderProfiles[member]?.phone && <div>Phone: {senderProfiles[member].phone}</div>}
                              </>
                            )}
                          </div>
                        ))}
                      </span>
                    </div>
                  )}

                  <div className="uc-detail-item">
                    <span className="uc-detail-label">Type:</span>
                    <span className="uc-detail-value">{isGuest ? "Guest Chat" : "User Chat"}</span>
                  </div>
                </div>
                <div className="uc-profile-actions">
                  <button className="uc-profile-action-btn" onClick={handleShowMedia}>
                    <IoDocumentOutline /> Media Visibility
                  </button>
                  <button
                    className="uc-profile-action-btn"
                    onClick={() => {
                      setShowSearchBar(true)
                      setShowProfileModal(false)
                    }}
                  >
                    <IoSearchOutline /> Search Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMediaModal && (
        <div className="uc-modal-overlay" onClick={() => setShowMediaModal(false)}>
          <div className="uc-modal-content uc-media-modal" onClick={(e) => e.stopPropagation()}>
            <div className="uc-modal-header">
              <div className="uc-tab-buttons">
                <button
                  className={activeMediaTab === "media" ? "active" : ""}
                  onClick={() => setActiveMediaTab("media")}
                >
                  Media
                </button>
                <button
                  className={activeMediaTab === "documents" ? "active" : ""}
                  onClick={() => setActiveMediaTab("documents")}
                >
                  Documents
                </button>
                <button
                  className={activeMediaTab === "links" ? "active" : ""}
                  onClick={() => setActiveMediaTab("links")}
                >
                  Links
                </button>
              </div>
              <button onClick={() => setShowMediaModal(false)}>
                <IoCloseOutline />
              </button>
            </div>

            <div className="uc-modal-body">
              {activeMediaTab === "media" && (
                <div className="uc-media-grid">
                  {mediaFiles.length > 0 ? (
                    mediaFiles.map((file, index) => (
                      <img
                        key={index}
                        src={file.url || "/placeholder.svg"}
                        alt={file.name}
                        className="uc-media-item"
                        onClick={() => handleDocumentClick(file)}
                      />
                    ))
                  ) : (
                    <p>No media files</p>
                  )}
                </div>
              )}
              {activeMediaTab === "documents" && (
                <div className="uc-doc-list">
                  {docFiles.length > 0 ? (
                    docFiles.map((file, index) => (
                      <div key={index} className="uc-doc-item" onClick={() => handleDocumentClick(file)}>
                        <IoDocumentOutline />
                        <span>{file.name}</span>
                      </div>
                    ))
                  ) : (
                    <p>No documents</p>
                  )}
                </div>
              )}

              {activeMediaTab === "links" && (
                <div className="uc-link-list">
                  {links.length > 0 ? (
                    links.map((link, index) => (
                      <a key={index} href={link} target="_blank" rel="noopener noreferrer" className="uc-link-item">
                        {link}
                      </a>
                    ))
                  ) : (
                    <p>No links</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {documentOverlay && (
        <div className="uc-document-overlay" onClick={() => setDocumentOverlay(null)}>
          <div className="uc-document-overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="uc-document-overlay-header">
              <h4>{documentOverlay.name}</h4>
              <button onClick={() => setDocumentOverlay(null)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="uc-document-overlay-body">
              {documentOverlay.type.startsWith("image/") ? (
                <img
                  src={documentOverlay.url || "/placeholder.svg"}
                  alt={documentOverlay.name}
                  className="uc-overlay-image"
                />
              ) : (
                <div className="uc-overlay-document">
                  <IoDocumentOutline className="uc-overlay-document-icon" />
                  <p>{documentOverlay.name}</p>
                  <p className="uc-overlay-document-size">{(documentOverlay.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              )}
            </div>
            <div className="uc-document-overlay-footer">
              <button className="uc-download-overlay-btn" onClick={() => handleDownloadFile(documentOverlay)}>
                <IoDownloadOutline /> Download & Open
              </button>
            </div>
          </div>
        </div>
      )}

     {deleteModalVisible && deleteMsg && (
  <div className="delete-uc-modal-overlay" onClick={() => setDeleteModalVisible(false)}>
    <div className="delete-uc-modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="delete-uc-modal-header">
        <h3>Delete Message</h3>
        <button onClick={() => setDeleteModalVisible(false)}>
          <IoCloseOutline />
        </button>
      </div>
      <div className="delete-uc-modal-body">
        <button onClick={handleDeleteForMe}>Delete for me</button>

        {/* ────────────────────────────────────────────── */}
        {/* Show "Delete for everyone" ONLY if this is YOUR message */}
        {(() => {
          const isOwnMessage = selectedChat.isGroup
            ? deleteMsg.sender === currentUid
            : (deleteMsg.sender === "user" || deleteMsg.sender === "guest");

          const isRecent = Date.now() - getTimestampMs(deleteMsg.timestamp) < 3600000; // 1 hour

          if (isOwnMessage && isRecent) {
            return (
              <button onClick={handleDeleteForEveryone}>
                Delete for everyone
              </button>
            );
          }
          return null;
        })()}

        <button onClick={() => setDeleteModalVisible(false)}>Cancel</button>
      </div>
    </div>
  </div>
)}
     {showMessageActions && selectedMessage && (
  <>
    <div 
      className="uc-action-backdrop" 
      onClick={() => {
        setShowMessageActions(false);
        setSelectedMessage(null);
      }} 
    />

    <div className="uc-message-action-menu uc-centered-action-menu">
      <div className="uc-action-header">
        <span>Message Options</span>
        <button 
          className="uc-action-close" 
          onClick={() => {
            setShowMessageActions(false);
            setSelectedMessage(null);
          }}
        >
          ×
        </button>
      </div>

      <div className="uc-action-buttons">
        {/* Always available */}
        <button 
          className="uc-action-btn reply" 
          onClick={() => handleMessageAction("reply")}
        >
          <FaReply /> Reply
        </button>

        {/* Copy button ONLY if message has text */}
        {(selectedMessage.message || selectedMessage.reply) && (
          <button 
            className="uc-action-btn copy" 
            onClick={() => handleMessageAction("copy")}
          >
            Copy Text
          </button>
        )}

        {/* Edit & Delete Logic */}
        {(() => {
          const isOwnMessage = selectedChat.isGroup
            ? selectedMessage.sender === currentUid
            : (selectedMessage.sender === "user" || selectedMessage.sender === "guest");

          if (!isOwnMessage) {
            // Supplier / Other user's message
            return (
              <button 
                className="uc-action-btn delete" 
                onClick={() => handleMessageAction("delete")}
              >
                Delete for me
              </button>
            );
          }

          // Own message
          return (
            <>
              {Date.now() - getTimestampMs(selectedMessage.timestamp) < 15 * 60 * 1000 && (
                <button 
                  className="uc-action-btn edit" 
                  onClick={() => handleMessageAction("edit")}
                >
                  Edit Message
                </button>
              )}

              <button 
                className="uc-action-btn delete" 
                onClick={() => handleMessageAction("delete")}
              >
                Delete
              </button>
            </>
          );
        })()}

        {/* Cancel Button - Always visible */}
        <button 
          className="uc-action-btn cancel" 
          onClick={() => {
            setShowMessageActions(false);
            setSelectedMessage(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </>
)}

      </div>
 
  )
}

export default Chatbot